import type { PalletType, ServiceKey } from "./types";

export interface Country {
  c: string;
  n: string;
}

export const COUNTRIES: Country[] = [
  { c: "NL", n: "Netherlands" }, { c: "DE", n: "Germany" }, { c: "BE", n: "Belgium" },
  { c: "FR", n: "France" }, { c: "AT", n: "Austria" }, { c: "BG", n: "Bulgaria" },
  { c: "HR", n: "Croatia" }, { c: "CY", n: "Cyprus" }, { c: "CZ", n: "Czech Republic" },
  { c: "DK", n: "Denmark" }, { c: "EE", n: "Estonia" }, { c: "FI", n: "Finland" },
  { c: "GR", n: "Greece" }, { c: "HU", n: "Hungary" }, { c: "IE", n: "Ireland" },
  { c: "IT", n: "Italy" }, { c: "LV", n: "Latvia" }, { c: "LT", n: "Lithuania" },
  { c: "LU", n: "Luxembourg" }, { c: "MT", n: "Malta" }, { c: "PL", n: "Poland" },
  { c: "PT", n: "Portugal" }, { c: "RO", n: "Romania" }, { c: "SK", n: "Slovakia" },
  { c: "SI", n: "Slovenia" }, { c: "ES", n: "Spain" }, { c: "SE", n: "Sweden" },
  { c: "NO", n: "Norway" }, { c: "CH", n: "Switzerland" }, { c: "GB", n: "United Kingdom" },
  { c: "AL", n: "Albania" }, { c: "BA", n: "Bosnia" }, { c: "EN", n: "England" },
  { c: "GE", n: "Georgia" }, { c: "IS", n: "Iceland" }, { c: "KZ", n: "Kazakhstan" },
  { c: "KO", n: "Kosovo" }, { c: "ME", n: "Montenegro" }, { c: "MK", n: "Northern Macedonia" },
  { c: "UA", n: "Ukraine" }, { c: "UZ", n: "Uzbekistan" }, { c: "SC", n: "Scotland" },
  { c: "RS", n: "Serbia" }, { c: "TR", n: "Turkey" }, { c: "TM", n: "Turkmenistan" },
];

// Provisional sanity bounds (standard EU trailer: ~13.6 m load length, ~2.48 m
// interior width, ~2.7 m interior height, ~24 t payload).
export const LIMITS = {
  totalWeightKg: { min: 1, max: 25000 },
  qty: { min: 1, max: 10000 },
  heightCm: { min: 1, max: 270 },
  lengthCm: { min: 1, max: 1360 },
  widthCm: { min: 1, max: 248 },
  ldm: { min: 0.01, max: 13.6 },
} as const;

export const PALLET_LABELS: Record<PalletType, string> = {
  euro: "Euro (120\u00d780)",
  block: "Block (120\u00d7100)",
  ibc: "IBC (120\u00d7100)",
  cp3: "CP3 (115\u00d7115)",
  half: "Half pallet",
  size80x60: "80\u00d760",
  size120x120: "120\u00d7120",
  size150x80: "150\u00d780",
  custom: "Custom",
  unspecified: "LDM",
};

export const PALLET_TYPE_OPTIONS: Record<PalletType, string> = {
  euro: "Euro",
  block: "Block",
  ibc: "IBC",
  cp3: "CP3",
  half: "Half pallet",
  size80x60: "80\u00d760",
  size120x120: "120\u00d7120",
  size150x80: "150\u00d780",
  custom: "Custom",
  unspecified: "LDM",
};

export type PalletFootprintType = Exclude<PalletType, "custom" | "unspecified" | "half">;

export const PALLET_FOOTPRINTS: Record<PalletFootprintType, { length: number; width: number }> = {
  euro: { length: 120, width: 80 },
  block: { length: 120, width: 100 },
  ibc: { length: 120, width: 100 },
  cp3: { length: 115, width: 115 },
  size80x60: { length: 80, width: 60 },
  size120x120: { length: 120, width: 120 },
  size150x80: { length: 150, width: 80 },
};

const CORE_PALLET_TYPES: PalletType[] = ["euro", "block", "ibc", "cp3", "half"];
const OPEN_PALLET_TYPES: PalletType[] = ["custom", "unspecified"];

export const DEFAULT_PALLET_TYPES: PalletType[] = [...CORE_PALLET_TYPES, ...OPEN_PALLET_TYPES];

export const BENELUX_PALLET_TYPES: PalletType[] = [
  ...CORE_PALLET_TYPES,
  "size80x60",
  "size120x120",
  "size150x80",
  ...OPEN_PALLET_TYPES,
];

export const BENELUX_COUNTRY_CODES = ["NL", "BE", "LU"] as const;

export const HEIGHT_PRESETS_CM = ["80", "100", "120", "144", "160", "180", "200", "240"] as const;

export const QUICK_FILL_LIMITS = {
  maxFiles: 5,
  maxFileMb: 10,
} as const;

// Keep in sync with server Settings.quote_max_* defaults and
// QuoteAttachmentLimits.MAX_TOTAL_BYTES.
export const QUOTE_ATTACHMENT_LIMITS = {
  maxFiles: 5,
  maxFileMb: 2,
  maxTotalMb: 2.5,
  maxTotalBytes: 2_500_000,
} as const;

// Keep in sync with server/app/dtos/quote.py QuoteTextValidator.
export const QUOTE_TEXT_LIMITS = {
  maxSubject: 200,
  maxEmailText: 20_000,
} as const;

// Generic Neele-Vat name, used on the confirmation screen when a department
// config does not provide its own contact person.
export const FALLBACK_CONTACT = {
  name: "Neele-Vat",
};

export const PRIVACY_POLICY_URL = "https://www.neelevat.com/privacy/";

export const SPECIFIC_DATE_HINT =
  "For goods released on a specific date. Otherwise, we will strive for the fastest option.";

export const SPECIFIC_DELIVERY_DATE_HINT =
  "For goods delivered on a specific date. Otherwise, we will strive for the fastest option.";

export const ROUTE_LOCATION_HINT =
  "Enter a postcode or city for loading and for unloading.";

export const BENELUX_ROUTE_LOCATION_HINT =
  "Enter the full loading and unloading address.";

export const SERVICE_PILL_LABELS: Record<ServiceKey, string> = {
  adr: "ADR",
  temp: "Temperature",
  tail: "Tail lift",
  ftl: "FTL",
  time: "Time window",
  ref: "Reference / Notes",
  attachments: "Additional attachments",
};

export const SERVICE_PILL_ORDER: ServiceKey[] = [
  "adr",
  "temp",
  "tail",
  "ftl",
  "time",
  "ref",
  "attachments",
];

export const ADR_CLASSES = [
  "1 Explosives",
  "2 Gases",
  "3 Flammable liquids",
  "4 Flammable solids",
  "5 Oxidizing substances",
  "6 Toxic / Infectious",
  "7 Radioactive",
  "8 Corrosive",
  "9 Miscellaneous",
];

// City-to-country lookup for the offline text parser fallback.
export const CITY_COUNTRY: Record<string, string> = {
  rotterdam: "NL", amsterdam: "NL", utrecht: "NL", eindhoven: "NL",
  "den haag": "NL", "the hague": "NL", breda: "NL", tilburg: "NL",
  groningen: "NL", arnhem: "NL", nijmegen: "NL", maastricht: "NL",
  venlo: "NL", heerenveen: "NL", zwolle: "NL", almere: "NL",
  leiden: "NL", dordrecht: "NL", oss: "NL", moerdijk: "NL",
  berlin: "DE", hamburg: "DE", munich: "DE", "m\u00fcnchen": "DE",
  cologne: "DE", "k\u00f6ln": "DE", frankfurt: "DE", "d\u00fcsseldorf": "DE",
  dusseldorf: "DE", stuttgart: "DE", dortmund: "DE", essen: "DE",
  bremen: "DE", dresden: "DE", leipzig: "DE", hannover: "DE",
  nuremberg: "DE", "n\u00fcrnberg": "DE", duisburg: "DE", bochum: "DE",
  wuppertal: "DE", bielefeld: "DE", mannheim: "DE", karlsruhe: "DE",
  augsburg: "DE", wiesbaden: "DE", aachen: "DE",
  brussels: "BE", antwerp: "BE", antwerpen: "BE", ghent: "BE",
  gent: "BE", bruges: "BE", "li\u00e8ge": "BE", liege: "BE",
  charleroi: "BE", namur: "BE", leuven: "BE",
  paris: "FR", lyon: "FR", marseille: "FR", toulouse: "FR",
  nice: "FR", nantes: "FR", strasbourg: "FR", montpellier: "FR",
  bordeaux: "FR", lille: "FR", rennes: "FR", reims: "FR",
  "le havre": "FR", calais: "FR", dijon: "FR", rouen: "FR",
  vienna: "AT", wien: "AT", graz: "AT", linz: "AT", salzburg: "AT", innsbruck: "AT",
  warsaw: "PL", krakow: "PL", "krak\u00f3w": "PL", wroclaw: "PL",
  gdansk: "PL", "gda\u0144sk": "PL", poznan: "PL", "pozna\u0144": "PL",
  lodz: "PL", "\u0142\u00f3d\u017a": "PL", katowice: "PL", szczecin: "PL",
  prague: "CZ", praha: "CZ", brno: "CZ", ostrava: "CZ", plzen: "CZ",
  budapest: "HU", debrecen: "HU", szeged: "HU", miskolc: "HU",
  bucharest: "RO", cluj: "RO", timisoara: "RO", "timi\u0219oara": "RO", iasi: "RO", "ia\u0219i": "RO",
  rome: "IT", roma: "IT", milan: "IT", milano: "IT", naples: "IT",
  napoli: "IT", turin: "IT", torino: "IT", genoa: "IT", genova: "IT",
  palermo: "IT", bologna: "IT", florence: "IT", firenze: "IT", verona: "IT", venice: "IT",
  madrid: "ES", barcelona: "ES", valencia: "ES", seville: "ES",
  sevilla: "ES", malaga: "ES", bilbao: "ES", zaragoza: "ES",
  lisbon: "PT", lisboa: "PT", porto: "PT", faro: "PT",
  copenhagen: "DK", "k\u00f8benhavn": "DK", aarhus: "DK", odense: "DK",
  stockholm: "SE", gothenburg: "SE", "g\u00f6teborg": "SE", "malm\u00f6": "SE", malmo: "SE",
  oslo: "NO", bergen: "NO", trondheim: "NO", stavanger: "NO",
  helsinki: "FI", tampere: "FI", turku: "FI", espoo: "FI",
  dublin: "IE", cork: "IE", galway: "IE", limerick: "IE",
  london: "GB", manchester: "GB", birmingham: "GB", glasgow: "GB",
  leeds: "GB", liverpool: "GB", edinburgh: "GB", bristol: "GB",
  sheffield: "GB", newcastle: "GB", nottingham: "GB", cardiff: "GB",
  zurich: "CH", "z\u00fcrich": "CH", geneva: "CH", "gen\u00e8ve": "CH",
  basel: "CH", bern: "CH", lausanne: "CH",
  athens: "GR", thessaloniki: "GR", patras: "GR",
  sofia: "BG", plovdiv: "BG", varna: "BG",
  zagreb: "HR", split: "HR", rijeka: "HR", dubrovnik: "HR",
  bratislava: "SK", kosice: "SK", "ko\u0161ice": "SK",
  ljubljana: "SI", maribor: "SI",
  tallinn: "EE", tartu: "EE",
  riga: "LV", daugavpils: "LV",
  vilnius: "LT", kaunas: "LT", klaipeda: "LT", "klaip\u0117da": "LT",
  luxembourg: "LU",
  valletta: "MT",
  nicosia: "CY", limassol: "CY",
};

export const COUNTRY_NAMES: Record<string, string> = COUNTRIES.reduce(
  (acc, c) => {
    acc[c.n.toLowerCase()] = c.c;
    return acc;
  },
  {} as Record<string, string>,
);
